import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";

import {
  fetchCompanyById,
  updateCompany,
  resetStatus,
} from "../../store/slices/userSlice";

import { fetchCountries } from "../../store/slices/locationSlice";

import StatusPopup from "../../components/popups/StatusPopup";

import EditSuccessPopup from "../../components/popups/EditSuccessPopup";

import EditPresnolDetail from "../../components/form/EditPresnolDetail";

import EditPayTaxes from "../../components/form/EditPayTaxes";

import EditAction from "../../components/form/EditAction";

const EditCompany = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const location = useLocation();

  const fromPath = location.state?.from || "/dashboard";

  const id = location.state?.id;

  const EDIT_KEY = `editCompany_${id}`;

  // COMPANY DATA
  const selectedUser = useAppSelector(
    (s) => s.user.entityByKey[EDIT_KEY] || null,
  );

  const isInitialLoading = useAppSelector(
    (s) => s.user.entityLoadingByKey[EDIT_KEY] || false,
  );

  // UPDATE STATUS
  const {
    isSuccess,
    errorMessage,
    loading: isUpdating,
  } = useAppSelector((s) => s.user);

  // LOCATION
  const {
    countries = [],
    states = [],
    cities = [],
  } = useAppSelector((state) => state.location || {});

  const [companyFormData, setCompanyFormData] = useState({});

  // RESET STATUS
  useEffect(() => {
    dispatch(resetStatus());

    return () => dispatch(resetStatus());
  }, [dispatch]);

  // FETCH COMPANY
  useEffect(() => {
    if (id) {
      dispatch(
        fetchCompanyById({
          id,
          key: EDIT_KEY,
        }),
      );
    }
  }, [id, dispatch, EDIT_KEY]);

  // SET FORM DATA
  useEffect(() => {
    if (selectedUser) {
      setCompanyFormData(selectedUser);
    }
  }, [selectedUser]);

  // FETCH COUNTRIES
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // POPUP CLOSE
  const handlePopupClose = () => {
    if (isSuccess) {
      navigate(fromPath, {
        replace: true,
      });
    }

    dispatch(resetStatus());
  };

  // FORM DATA CHANGE
  const handleDataChange = (updatedData) => {
    setCompanyFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  // UPDATE
  const handleUpdate = () => {
    if (!id || !companyFormData) return;

    // const formData = new FormData();

    // formData.append("name", companyFormData.name || "");

    // formData.append("email", companyFormData.email || "");

    // formData.append("phone", companyFormData.phone || "");

    // formData.append("addressLine1", companyFormData.addressLine || "");

    // formData.append(
    //   "cityId",
    //   companyFormData.cityId || companyFormData.CityId || "",
    // );

    // formData.append("postalCode", companyFormData.postalCode || "");

    // formData.append("payrollCycle", companyFormData.payrollCycle || "");
    const formData = new FormData();

    formData.append("clientId", companyFormData.clientId || "");

    formData.append("name", companyFormData.name || "");
    formData.append("email", companyFormData.email || "");
    formData.append("phone", companyFormData.phone || "");
    formData.append(
      "addressLine",
      companyFormData.addressLine ||
        companyFormData.addressLine1 ||
        companyFormData.address ||
        "",
    );
    formData.append("postalCode", companyFormData.postalCode || "");

    formData.append(
      "countryId",
      companyFormData.countryId || companyFormData.CountryId || "",
    );
    formData.append(
      "stateId",
      companyFormData.stateId || companyFormData.StateId || "",
    );
    formData.append(
      "cityId",
      companyFormData.cityId || companyFormData.CityId || "",
    );

    formData.append("payrollCycle", companyFormData.payrollCycle || "");

    const finalTaxIds =
      companyFormData.taxIds ||
      (Array.isArray(companyFormData.formulas)
        ? companyFormData.formulas.map((f) =>
            typeof f === "object" ? f.id : f,
          )
        : []);

    finalTaxIds.forEach((id) => {
      formData.append("taxIds", id);
    });

    formData.append("newCountryName", companyFormData.newCountryName || "");

    formData.append("newStateName", companyFormData.newStateName || "");

    formData.append("newCityName", companyFormData.newCityName || "");

    if (companyFormData.file) {
      formData.append("logo", companyFormData.file);
    }

    formData.append("taxScheduleType", companyFormData.taxScheduleType || "");

    if (finalTaxIds.length) {
      finalTaxIds.forEach((id) => {
        formData.append("taxIds", id);
      });
    }

    // IMAGE
    if (companyFormData.file) {
      formData.append("file", companyFormData.file);
    }

    dispatch(
      updateCompany({
        id: Number(id),
        data: formData,
      }),
    );
  };

  // LOADER
  if (isInitialLoading && !selectedUser) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          height: "80vh",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>

        <h5 className="ms-3 mb-0">Fetching Company Data</h5>
      </div>
    );
  }

  return (
    <>
      <div className={isUpdating ? "opacity-50 pointer-events-none" : ""}>
        <EditAction
          clientData={companyFormData}
          onUpdate={handleUpdate}
          isLoading={isUpdating}
        />

        <div>
          {/* PERSONAL DETAIL */}
          <EditPresnolDetail
            clientData={companyFormData}
            onDataChange={handleDataChange}
            // LOCATION
            countries={countries}
            states={states}
            cities={cities}
          />

          {/* TAX */}
          <EditPayTaxes
            clientData={companyFormData}
            onDataChange={handleDataChange}
          />
        </div>
      </div>

      {/* SUCCESS */}
      <EditSuccessPopup
        show={isSuccess === true}
        onHide={handlePopupClose}
        entityType="company"
        operationType="update"
        title="Success"
      />

      {/* ERROR */}
      <StatusPopup
        show={!!errorMessage}
        type="error"
        title="Error"
        message={
          Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
        }
        handleClose={handlePopupClose}
      />
    </>
  );
};

export default EditCompany;
